import React, { useMemo } from 'react'
import { TextField } from '@carsys/form'
import { makeStyles } from '@mui/styles'

interface MultilineTextFieldProps {
  label: string
  name: string
  value: string
  className?: string
  minRows?: number
  required?: boolean
}

const useStyles = makeStyles(() => ({
  screenOnly: {
    '@media print': {
      display: 'none !important'
    }
  },
  printOnly: {
    display: 'none',
    '@media print': {
      display: 'block',
      marginTop: '12px'
    }
  },
  printMuiStyle: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 4,
    padding: '18.5px 14px',
    fontSize: '16px',
    lineHeight: 1.4375,
    color: 'rgba(0, 0, 0, 0.87)',
    position: 'relative',
    backgroundColor: 'white'
  },
  printContent: {
    whiteSpace: 'pre-wrap'
  }
}))

const useLabelStyles = makeStyles({
  printLabel: (props: { hasValue: boolean }) => ({
    position: 'absolute',
    left: '12px',
    top: props.hasValue ? '-9px' : '15%',
    transform: props.hasValue ? 'none' : 'translateY(-50%)',
    backgroundColor: 'white',
    padding: '0 4px',
    fontSize: props.hasValue ? '0.75rem' : '1rem',
    color: 'rgba(0, 0, 0, 0.6)'
  })
})

const MultilineTextFieldWithPrint: React.FC<MultilineTextFieldProps> = ({
  label,
  name,
  value,
  required = false,
  className = '',
  minRows = 4
}) => {
  const hasValue = !!value?.trim()
  const classes = useStyles()
  const labelClasses = useLabelStyles({ hasValue })

  const estimatedHeight = `${minRows * 24 + 37}px`

  const renderedField = useMemo(
    () => (
      <>
        {/* Normal screen view */}
        <div className={classes.screenOnly}>
          <TextField
            multiline
            className={className}
            required={required}
            label={label}
            name={name}
            minRows={minRows}
            fullWidth
            variant='outlined'
          />
        </div>

        {/* Print view */}
        <div
          className={`${className} ${classes.printOnly} ${classes.printMuiStyle}`}
          style={!hasValue ? { minHeight: estimatedHeight } : {}}
        >
          <label className={labelClasses.printLabel}>
            {label}
            {required && <span style={{ color: 'red' }}> *</span>}
          </label>
          <div className={classes.printContent}>{value || ''}</div>
        </div>
      </>
    ),
    [label, name, value, className, minRows, required, classes, labelClasses, hasValue, estimatedHeight]
  )

  return renderedField
}

export default MultilineTextFieldWithPrint
